import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import ArrowRightIcon from "lucide-react/dist/esm/icons/arrow-right";
import GitPullRequestIcon from "lucide-react/dist/esm/icons/git-pull-request";
import RefreshCwIcon from "lucide-react/dist/esm/icons/refresh-cw";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import { useId, useMemo, useState } from "react";

import { useDensity } from "@/components/data-table/use-density";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { StatusDot } from "@/components/status/status-badge";
import type { Tone } from "@/components/status/tone";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertQueue, SeverityLegend } from "@/features/operations/alert-queue";
import { scenariosFixture } from "@/features/operations/mock-data";
import { ScenarioHealthGrid } from "@/features/operations/scenario-health-grid";
import { StatusSummaryBar } from "@/features/operations/status-summary";
import { useAlertQueue } from "@/features/operations/use-alert-queue";
import { getPendingProposals } from "@/features/scenarios/definition";
import { healthQueryOptions } from "@/features/system/queries";
import { formatClock } from "@/lib/format";
import { useNow } from "@/lib/use-now";

export const Route = createFileRoute("/")({
  component: OverviewPage
});

/**
 * オペレーション系の画面。監視して即対応することが目的なので、
 * 密度を上げ、スクロールせずに全体を見渡せることを優先する（原則 11-2）。
 * 探索や期間比較は分析系の /analytics に分離している。
 */
function OverviewPage() {
  const { api } = Route.useRouteContext();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [toneFilter, setToneFilter] = useState<Tone | "all">("all");
  const { density } = useDensity();
  const autoRefreshId = useId();

  const now = useNow(30_000, autoRefresh);
  const health = useQuery({
    ...healthQueryOptions(api),
    refetchInterval: autoRefresh ? 30_000 : false
  });

  const { alerts, acknowledge, assignToMe } = useAlertQueue();
  const proposals = useMemo(() => getPendingProposals(), []);

  const counts = useMemo(
    () => [
      {
        tone: "critical" as const,
        label: "致命",
        count: alerts.filter((alert) => alert.severity === "critical").length
      },
      {
        tone: "warning" as const,
        label: "警告",
        count: alerts.filter((alert) => alert.severity === "warning").length
      },
      {
        tone: "info" as const,
        label: "情報",
        count: alerts.filter((alert) => alert.severity === "info").length
      }
    ],
    [alerts]
  );

  const visibleAlerts = useMemo(() => {
    if (toneFilter === "all") return alerts;
    return alerts.filter((alert) => alert.severity === toneFilter);
  }, [alerts, toneFilter]);

  const unhandledCount = alerts.filter((alert) => !alert.acknowledged).length;
  const attentionScenarios = scenariosFixture.filter(
    (scenario) => scenario.state === "paused" || scenario.state === "degraded"
  ).length;

  const connectionTone: Tone = health.isSuccess
    ? "success"
    : health.isError
      ? "critical"
      : "neutral";

  return (
    <>
      <PageHeader
        kind="operations"
        title="概況"
        description="全体状態の監視と、対応が必要な事象の処理"
        controls={
          <>
            <label
              htmlFor={autoRefreshId}
              className="flex items-center gap-1.5 text-xs text-fg-muted"
            >
              <Switch
                id={autoRefreshId}
                size="sm"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              自動更新
            </label>
            {/* データがいつ時点のものかを常に明示する（原則 11-2） */}
            <span className="num text-xs text-fg-subtle">最終更新 {formatClock(now)}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void health.refetch()}
              disabled={health.isFetching}
            >
              <RefreshCwIcon
                aria-hidden="true"
                className={health.isFetching ? "animate-spin" : undefined}
              />
              更新
            </Button>
          </>
        }
      />

      <StatusSummaryBar
        counts={counts}
        activeTone={toneFilter}
        onSelectTone={setToneFilter}
        trailing={
          <span className="flex items-center gap-1.5 text-xs text-fg-muted">
            <StatusDot
              tone={connectionTone}
              pulse={health.isError}
              label={health.isSuccess ? "API 接続正常" : "API 接続に問題あり"}
            />
            {health.isPending ? "Worker を確認中…" : null}
            {health.isSuccess ? `Worker v${health.data.version}` : null}
            {health.isError ? (
              <span className="text-critical-fg">Worker 応答なし · {health.error.message}</span>
            ) : null}
          </span>
        }
      />

      <PageBody density="dense" className="grid gap-4">
        <Panel
          title="要対応キュー"
          count={visibleAlerts.length}
          description={`未確認 ${unhandledCount}件`}
          actions={<SeverityLegend />}
          flush
        >
          <AlertQueue
            alerts={visibleAlerts}
            now={now}
            density={density}
            onAcknowledge={acknowledge}
            onAssign={assignToMe}
            compact
            maxHeight="min(52vh, 520px)"
          />
        </Panel>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <Panel
            title="シナリオの稼働状況"
            count={scenariosFixture.length}
            description={
              attentionScenarios > 0 ? `${attentionScenarios}件が停止または劣化` : "全件が正常稼働"
            }
          >
            <ScenarioHealthGrid scenarios={scenariosFixture} />
          </Panel>

          {/* AIOps（第4の軸）の横断入口。承認待ちのAI提案をここに集約する。 */}
          <Panel
            title="AIの提案（承認待ち）"
            count={proposals.length}
            description="週次レビューと配信改善から生成"
            flush
          >
            {proposals.length > 0 ? (
              <ul className="divide-y divide-line">
                {proposals.map((proposal) => (
                  <li key={`${proposal.scenarioId}-${proposal.kind}`}>
                    <Link
                      to="/scenarios/$scenarioId"
                      params={{ scenarioId: proposal.scenarioId }}
                      search={{ tab: "improve" }}
                      className="flex items-start gap-2.5 px-4 py-3 transition-colors hover:bg-surface-hover"
                    >
                      <span
                        className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-md bg-info-subtle text-info-fg"
                        aria-hidden="true"
                      >
                        {proposal.kind === "delivery" ? (
                          <SparklesIcon className="size-3" />
                        ) : (
                          <GitPullRequestIcon className="size-3" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-fg">
                          {proposal.scenarioName}
                        </span>
                        <span className="mt-0.5 block text-xs text-fg-subtle">
                          {proposal.summary}
                        </span>
                      </span>
                      <ArrowRightIcon
                        className="mt-1 size-3.5 shrink-0 text-fg-subtle"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-center text-sm text-fg-muted">
                承認待ちの提案はありません。
              </p>
            )}
          </Panel>
        </div>
      </PageBody>
    </>
  );
}
